import { useState, useEffect, useCallback } from 'react';
import { vendedoresService } from '../services';
import { useDebounce } from './useDebounce';

/**
 * Hook personalizado para manejar operaciones CRUD de vendedores
 */
export const useVendedores = () => {
  const [vendedores, setVendedores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  /**
   * Transformar datos de la API al formato esperado
   */
  const transformVendedorFromAPI = useCallback((vendedor) => ({
    id: vendedor.id,
    nombre: vendedor.full_name || vendedor.email || '',
    email: vendedor.email || '',
    telefono: vendedor.phone || '',
    direccion: vendedor.address || '',
    rol: vendedor.role || 'USER',
    estado: vendedor.is_active ? 'activo' : 'inactivo',
    fechaRegistro: vendedor.created_at || '',
    ultimoAcceso: vendedor.last_login || '',
    // Campos específicos de vendedor
    comisionPorcentaje: vendedor.commission_percentage || 0,
    periodoPago: vendedor.payment_period || 'monthly',
    metodoPago: vendedor.payment_method || 'transfer',
    informacionBancaria: vendedor.bank_info || '',
    clientesAsignados: vendedor.assigned_clients_count || 0,
    ventasMes: vendedor.sales_this_month || 0,
    comisionesMes: vendedor.commissions_this_month || 0,
    ventasAno: vendedor.sales_this_year || 0,
    comisionesAno: vendedor.commissions_this_year || 0,
    rendimiento: vendedor.performance_rating || 'bueno',
    timezone: vendedor.timezone || 'UTC',
    idioma: vendedor.language || 'es'
  }), []);

  /**
   * Cargar todos los vendedores
   */
  const loadVendedores = useCallback(async (params = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Parámetros por defecto para la consulta
      const defaultParams = {
        skip: 0,
        limit: 50,
        is_active: true,
        role: 'sales',
        is_seller: true, // Solo vendedores registrados explícitamente
        ...params
      };
      
      const result = await vendedoresService.getVendedores(defaultParams);
      
      if (result.success) {
        // Transformar los datos de la API al formato esperado
        const vendedoresData = result.data.map(transformVendedorFromAPI);
        setVendedores(vendedoresData);
      } else {
        setError(result.message || 'Error al cargar los vendedores');
        setVendedores([]);
      }
    } catch (err) {
      setError('Error de conexión al cargar los vendedores');
      console.error('Error loading vendedores:', err);
      setVendedores([]);
    } finally {
      setIsLoading(false);
    }
  }, [transformVendedorFromAPI]);

  /**
   * Buscar vendedores (actualiza query, la búsqueda real se hace con debounce)
   */
  const searchVendedores = useCallback((query) => {
    setSearchQuery(query);
  }, []);
  
  /**
   * Realizar búsqueda real en la API (se ejecuta con debounce)
   */
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Si no hay búsqueda, cargar todos los vendedores
      loadVendedores();
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Parámetros de búsqueda para la API
      const searchParams = {
        skip: 0,
        limit: 50,
        is_active: true,
        role: 'sales',
        is_seller: true,
        search: query
      };
      
      const result = await vendedoresService.getVendedores(searchParams);
      
      if (result.success) {
        const vendedoresData = result.data.map(transformVendedorFromAPI);
        setVendedores(vendedoresData);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setVendedores([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching vendedores:', err);
      setVendedores([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadVendedores, transformVendedorFromAPI]);

  /**
   * Eliminar vendedor
   */
  const deleteVendedor = useCallback(async (vendedorId) => {
    try {
      const result = await vendedoresService.deleteVendedor(vendedorId);
      
      if (result.success) {
        // Actualizar la lista local
        const updatedVendedores = vendedores.filter(v => v.id !== vendedorId);
        setVendedores(updatedVendedores);
        
        return {
          success: true,
          message: 'Vendedor eliminado exitosamente'
        };
      } else {
        return {
          success: false,
          error: result.message || 'Error al eliminar el vendedor'
        };
      }
    } catch (err) {
      console.error('Error deleting vendedor:', err);
      return {
        success: false,
        error: 'Error inesperado al eliminar el vendedor'
      };
    }
  }, [vendedores]);

  /**
   * Obtener estadísticas de vendedores
   */
  const getVendedoresStats = useCallback(() => {
    const stats = {
      total: vendedores.length,
      activos: vendedores.filter(v => v.estado === 'activo').length,
      conClientes: vendedores.filter(v => v.clientesAsignados > 0).length,
      totalVentasMes: vendedores.reduce((sum, v) => sum + (v.ventasMes || 0), 0),
      totalComisionesMes: vendedores.reduce((sum, v) => sum + (v.comisionesMes || 0), 0),
      rendimientoPromedio: vendedores.length > 0 
        ? vendedores.reduce((sum, v) => {
            const scores = { excelente: 5, muy_bueno: 4, bueno: 3, regular: 2, bajo: 1 };
            return sum + (scores[v.rendimiento] || 3);
          }, 0) / vendedores.length
        : 0
    };
    return stats;
  }, [vendedores]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar vendedores al montar el componente
  useEffect(() => {
    loadVendedores();
  }, [loadVendedores]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, performSearch]);

  return {
    vendedores,
    isLoading,
    error,
    searchQuery,
    loadVendedores,
    deleteVendedor,
    searchVendedores,
    getVendedoresStats,
    clearError
  };
};

/**
 * Hook para manejar un vendedor individual (crear/editar)
 */
export const useVendedor = (vendedorId = null) => {
  const [vendedor, setVendedor] = useState({
    // Información básica REQUERIDA
    fullName: '',
    email: '',
    phone: '',
    phones: [],
    address: '',

    // Campos de dirección desglosados (el backend guarda todo en `address` como JSON)
    street: '',
    unit: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Estados Unidos',

    // Contraseña (solo para crear)
    password: '',

    // Información de usuario
    role: 'sales', // sales por defecto para vendedores
    isActive: true,

    // Información específica de vendedor
    commissionPercentage: 0,
    paymentPeriod: 'MONTHLY', // WEEKLY, BIWEEKLY, MONTHLY
    paymentMethod: 'BANK_TRANSFER', // CHECK, BANK_TRANSFER, ZELLE

    // Información bancaria
    bankName: '',
    bankAccount: '',
    bankRouting: '',
    bankSwift: '',

    // Campos adicionales
    hireDate: '',
    employeeId: '',

    // Configuración
    timezone: 'America/Bogota',
    language: 'es',

    // Metadatos
    notes: ''
  });
  
  const [originalVendedor, setOriginalVendedor] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const isEditing = Boolean(vendedorId);

  /**
   * Cargar datos del vendedor (solo en modo edición)
   */
  const loadVendedor = useCallback(async () => {
    if (!vendedorId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getVendedor(vendedorId);
      
      if (result.success) {
        // El backend guarda la dirección como string JSON (o texto plano legacy).
        // Intentar parsearla; si falla, dejarla como texto en `street` para no perderla.
        let addressFields = {
          street: '', unit: '', city: '', state: '', postalCode: '', country: 'Estados Unidos'
        };
        if (result.data.address) {
          try {
            const parsed = JSON.parse(result.data.address);
            addressFields = {
              street:     parsed.street      || '',
              unit:       parsed.unit        || '',
              city:       parsed.city        || '',
              state:      parsed.state       || '',
              postalCode: parsed.postal_code || '',
              country:    parsed.country     || 'Estados Unidos'
            };
          } catch (e) {
            // No era JSON: tratar como texto plano en la calle
            addressFields.street = result.data.address;
          }
        }

        // Transformar los datos de la API al formato esperado por el formulario
        const vendedorData = {
          id: result.data.id,
          fullName: result.data.full_name || '',
          email: result.data.email || '',
          phone: result.data.phone || '',
          phones: result.data.phones || [],
          address: result.data.address || '',
          ...addressFields,
          role: result.data.role || 'sales',
          isActive: result.data.is_active !== false,
          commissionPercentage: result.data.commission_percentage || 0,
          paymentPeriod: result.data.payment_period || 'MONTHLY',
          paymentMethod: result.data.payment_method || 'BANK_TRANSFER',
          bankName: result.data.bank_name || '',
          bankAccount: result.data.bank_account || '',
          bankRouting: result.data.bank_routing || '',
          bankSwift: result.data.bank_swift || '',
          hireDate: result.data.hire_date || '',
          employeeId: result.data.employee_id || '',
          timezone: result.data.timezone || 'America/Bogota',
          language: result.data.language || 'es',
          notes: result.data.notes || ''
        };

        setVendedor(vendedorData);
        setOriginalVendedor({ ...vendedorData });
      } else {
        setError(result.message || 'Error al cargar los datos del vendedor');
      }
    } catch (err) {
      setError('Error de conexión al cargar los datos del vendedor');
      console.error('Error loading vendedor:', err);
    } finally {
      setIsLoading(false);
    }
  }, [vendedorId]);

  /**
   * Actualizar datos del vendedor
   */
  const updateVendedorData = useCallback((field, value) => {
    setVendedor(prev => {
      const updated = { ...prev, [field]: value };
      
      // Verificar si hay cambios
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(originalVendedor);
      setHasChanges(hasChanges);
      
      return updated;
    });
  }, [originalVendedor]);

  /**
   * Guardar vendedor (crear o actualizar)
   */
  const saveVendedor = useCallback(async () => {
    try {
      // Serializar los campos de dirección como JSON string (formato esperado por backend
      // y consumido por VendedorDetalle.jsx). Si no hay ningún campo, mandar null.
      const hasAddressData = !!(vendedor.street || vendedor.unit || vendedor.city ||
                                vendedor.state || vendedor.postalCode);
      const addressPayload = hasAddressData
        ? JSON.stringify({
            street:      vendedor.street      || '',
            unit:        vendedor.unit        || '',
            city:        vendedor.city        || '',
            state:       vendedor.state       || '',
            postal_code: vendedor.postalCode  || '',
            country:     vendedor.country     || 'Estados Unidos'
          })
        : (vendedor.address || null);

      // Preparar datos para enviar a la API
      const vendedorData = {
        full_name: vendedor.fullName,
        email: vendedor.email,
        phone: vendedor.phone || null,
        phones: vendedor.phones && vendedor.phones.length > 0 ? vendedor.phones : null,
        address: addressPayload,
        role: vendedor.role || 'sales',
        is_active: vendedor.isActive !== false,
        commission_percentage: vendedor.commissionPercentage || 0,
        payment_period: vendedor.paymentPeriod || 'MONTHLY',
        payment_method: vendedor.paymentMethod || 'BANK_TRANSFER',
        bank_name: vendedor.bankName || null,
        bank_account: vendedor.bankAccount || null,
        bank_routing: vendedor.bankRouting || null,
        bank_swift: vendedor.bankSwift || null,
        hire_date: vendedor.hireDate || null,
        employee_id: vendedor.employeeId || null,
        timezone: vendedor.timezone || 'America/Bogota',
        language: vendedor.language || 'es',
        notes: vendedor.notes || null
      };
      
      // Agregar contraseña solo si estamos creando un nuevo vendedor
      if (!isEditing && vendedor.password) {
        vendedorData.password = vendedor.password;
      }
      
      // Limpiar campos undefined/null donde no se permiten
      Object.keys(vendedorData).forEach(key => {
        if (vendedorData[key] === undefined) {
          delete vendedorData[key];
        }
      });
      
      console.log('Datos a enviar a la API:', vendedorData);
      
      let result;
      
      if (isEditing) {
        // Actualizar vendedor existente
        result = await vendedoresService.updateVendedor(vendedorId, vendedorData);
      } else {
        // Crear nuevo vendedor
        result = await vendedoresService.createVendedor(vendedorData);
      }
      
      if (result.success) {
        return {
          success: true,
          message: result.message || `Vendedor ${vendedor.fullName} ${isEditing ? 'actualizado' : 'creado'} exitosamente`,
          data: result
        };
      } else {
        return {
          success: false,
          error: result.message || 'Error al guardar el vendedor',
          validation_errors: result.validation_errors || [],
          validationErrors: result.validationErrors || {}
        };
      }
    } catch (err) {
      console.error('Error saving vendedor:', err);
      return {
        success: false,
        error: 'Error de conexión al guardar el vendedor'
      };
    }
  }, [vendedor, isEditing, vendedorId]);

  /**
   * Resetear formulario
   */
  const resetVendedor = useCallback(() => {
    if (isEditing) {
      setVendedor({ ...originalVendedor });
    } else {
      setVendedor({
        fullName: '',
        email: '',
        phone: '',
        phones: [],
        address: '',
        street: '',
        unit: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'Estados Unidos',
        password: '',
        role: 'sales',
        isActive: true,
        commissionPercentage: 0,
        paymentPeriod: 'MONTHLY',
        paymentMethod: 'BANK_TRANSFER',
        bankName: '',
        bankAccount: '',
        bankRouting: '',
        bankSwift: '',
        hireDate: '',
        employeeId: '',
        timezone: 'America/Bogota',
        language: 'es',
        notes: ''
      });
    }
    setHasChanges(false);
  }, [isEditing, originalVendedor]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cargar vendedor al montar (solo en modo edición)
  useEffect(() => {
    if (isEditing) {
      loadVendedor();
    }
  }, [isEditing, loadVendedor]);

  return {
    vendedor,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateVendedorData,
    saveVendedor,
    resetVendedor,
    clearError
  };
};
